import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ScheduleService } from '../services/schedule.service';
import { SpinnerService } from '../services/spinner.service';
import { NotificationService } from '../notification/notification.service';
import { catchError, of } from 'rxjs';

export type ScheduleType = 'WORKING' | 'ON_SITE_AND_TAKEAWAY' | 'DELIVERY';

@Injectable({
  providedIn: 'root'
})
export class PublicScheduleStore {
  private scheduleService = inject(ScheduleService);
  private spinnerService = inject(SpinnerService);
  private notificationService = inject(NotificationService);
  private platformId = inject(PLATFORM_ID);

  private state = signal<{
    data: Partial<Record<ScheduleType, any>>;
    loading: Partial<Record<ScheduleType, boolean>>;
    error: Partial<Record<ScheduleType, any>>;
  }>({
    data: {},
    loading: {},
    error: {}
  });

  readonly schedule = computed(() => this.state().data['WORKING'] ?? null);
  readonly loading = computed(() => this.state().loading['WORKING'] ?? false);
  readonly error = computed(() => this.state().error['WORKING'] ?? null);

  readonly onSiteAndTakeawaySchedule = computed(() => this.state().data['ON_SITE_AND_TAKEAWAY'] ?? null);
  readonly deliverySchedule = computed(() => this.state().data['DELIVERY'] ?? null);

  constructor() {
    this.loadSchedule();
  }

  loadSchedule(type: ScheduleType = 'WORKING', force = false) {
    if (!force && this.state().data[type]) return;

    const isBrowser = isPlatformBrowser(this.platformId);
    if (isBrowser) {
      this.spinnerService.show();
    }

    this.state.update(s => ({
      ...s,
      loading: { ...s.loading, [type]: true },
      error: { ...s.error, [type]: null }
    }));

    this.scheduleService.getPublicSchedule(type).pipe(
      catchError((err) => {
        this.notificationService.errorHandler(err);
        this.state.update(s => ({
          ...s,
          error: { ...s.error, [type]: 'Erreur de chargement' },
          loading: { ...s.loading, [type]: false }
        }));
        if (isBrowser) this.spinnerService.hide();
        return of(null);
      })
    ).subscribe((result) => {
      if (isBrowser) this.spinnerService.hide();
      if (!result) return;

      this.state.update(s => ({
        ...s,
        data: { ...s.data, [type]: result?.body?.data ?? null },
        loading: { ...s.loading, [type]: false }
      }));
    });
  }

  refreshAll() {
    (['WORKING', 'ON_SITE_AND_TAKEAWAY', 'DELIVERY'] as ScheduleType[])
      .forEach(type => this.loadSchedule(type, true));
  }
}
